/* eslint-disable @typescript-eslint/no-require-imports */
import flattenColorPalette from 'tailwindcss/lib/util/flattenColorPalette';
import plugin from 'tailwindcss/plugin';

export const tailwindConfig = {
  content: ['./src/**/*.{js,ts,jsx,tsx,mdx}'],
  darkMode: 'selector',
  theme: {
    screens: {
      xxs: '480px',
      xs: '575px',
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
    },
    container: {
      center: true,
      padding: {
        DEFAULT: '1rem',
        xs: '2rem',
        lg: '3rem',
        xl: '4rem',
        '2xl': '6rem',
      },
    },
    colors: {
      transparent: 'transparent',
      primary: 'var(--color-brand-primary)',
      accent: 'var(--color-brand-accent)',
      black: '#000000',
      white: '#ffffff',
      yellow: '#ffd100',
      orange: '#F8971D',
      beige: {
        DEFAULT: '#A88C70',
        50: '#F5F0EB',
        100: '#ECE1D7',
        200: '#DBC9B9',
        300: '#CCB5A0',
        400: '#BCA188',
        500: '#A88C70',
        600: '#8E7256',
        700: '#735A40',
        800: '#57432E',
        900: '#3C2E20',
      },
      lilac: {
        DEFAULT: '#A885AE',
        50: '#F5EFF6',
        100: '#EBDFED',
        200: '#DBC5DD',
        300: '#CBB0CF',
        400: '#BB9BC0',
        500: '#A885AE',
        600: '#8E6B94',
        700: '#745379',
        800: '#583D5C',
        900: '#3C2A3F',
      },
      green: {
        DEFAULT: '#7D968C',
        50: '#EDF2F0',
        100: '#DCE4E1',
        200: '#C1CFC9',
        300: '#AABDB5',
        400: '#94ABA2',
        500: '#7D968C',
        600: '#637C72',
        700: '#4C635A',
        800: '#384B43',
        900: '#26332E',
      },
      blue: {
        DEFAULT: '#7C92B2',
        50: '#EEF1F7',
        100: '#DCE3EE',
        200: '#C1CDE0',
        300: '#AABAD2',
        400: '#94A7C4',
        500: '#7C92B2',
        600: '#617998',
        700: '#49607D',
        800: '#35485F',
        900: '#253141',
      },
      gray: {
        DEFAULT: '#909191',
        50: '#F0F1F1',
        100: '#E2E2E2',
        200: '#CBCCCC',
        300: '#B8B9B9',
        400: '#A5A6A6',
        500: '#909191',
        600: '#767777',
        700: '#5D5E5E',
        800: '#464747',
        900: '#303030',
      },
      success: {
        DEFAULT: '#157F11',
        50: '#E9F5E5',
        100: '#C6E6BB',
        200: '#A1D492',
        300: '#7DC16C',
        400: '#5AAD4A',
        500: '#37962B',
        600: '#157F11',
        700: '#006800',
        800: '#004F00',
        900: '#043900',
      },
      error: {
        DEFAULT: '#CA3825',
        50: '#FFECE6',
        100: '#FFCEBE',
        200: '#FFAD97',
        300: '#FF8E74',
        400: '#FB6F55',
        500: '#E5513A',
        600: '#CA3825',
        700: '#AA2417',
        800: '#83180E',
        900: '#5F1409',
      },
      focus: '#0071E3',
    },
    extend: {
      colors: {
        infographicBgGrey1: '#f4f4f4',
      },
      textColor: {
        'on-brand': 'var(--color-text-on-brand)',
      },
      animation: {
        slideup: 'slideup 300ms ease-in-out',
        slidedown: 'slidedown 300ms ease-in-out',
      },
      backgroundOpacity: {
        8: '0.08',
        12: '0.12',
        24: '0.24',
      },
      borderOpacity: {
        8: '0.08',
        12: '0.12',
        24: '0.24',
      },
      boxShadow: {
        tint10: '0 0 0 3px rgb(123,145,169, 1)',
        shadow25: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
        shadow10: '0px 4px 6px -1px rgba(0, 0, 0, 0.10)',
        shadow05: '0px 2px 4px -2px rgba(0, 0, 0, 0.05)',
      },
      keyframes: {
        slideup: {
          '0%': { 'grid-template-rows': '1fr' },
          '100%': { 'grid-template-rows': '0fr' },
        },
        slidedown: {
          '0%': { 'grid-template-rows': '0fr' },
          '100%': { 'grid-template-rows': '1fr' },
        },
      },
    },
  },

  plugins: [
    require('tailwindcss-animate'),
    require('tailwindcss-unimportant'),
    plugin(function ({ addComponents, theme, matchUtilities }) {
      matchUtilities(
        {
          bgl: (value) => ({
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              top: '0',
              display: 'block',
              backgroundColor: value,
              height: '100%',
              position: 'absolute',
              width: 'calc(100vw + 2px)',
              left: '-100vw',
            },
          }),
          bgr: (value) => ({
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              top: '0',
              display: 'block',
              backgroundColor: value,
              height: '100%',
              position: 'absolute',
              width: 'calc(100vw + 2px)',
              right: '0',
            },
          }),
          bgfull: (value) => ({
            position: 'relative',
            overflow: 'visible',
            '&::after': {
              zIndex: '-1',
              content: '""',
              top: '0',
              display: 'block',
              backgroundColor: value,
              height: '100%',
              position: 'absolute',
              width: 'calc(200vw + 2px)',
              left: '-100vw',
            },
          }),
        },
        {
          values: flattenColorPalette(theme('colors')),
        }
      );
      addComponents({
        '.container': {
          maxWidth: '100%',
          '@screen 2xl': {
            maxWidth: '1536px',
          },
        },

        '.topHeader': {
          boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.16)',
        },

        '.button': {
          boxShadow: '0 0 0 3px #87cefa',
        },

        '.container.py': {
          paddingTop: theme('spacing.8'),
          paddingBottom: theme('spacing.8'),
          '@screen sm': {
            paddingTop: theme('spacing.12'),
            paddingBottom: theme('spacing.12'),
          },
          '@screen lg': {
            paddingTop: theme('spacing.16'),
            paddingBottom: theme('spacing.16'),
          },
          '@screen xl': {
            paddingTop: theme('spacing.20'),
            paddingBottom: theme('spacing.20'),
          },
          '@screen 2xl': {
            paddingTop: theme('spacing.24'),
            paddingBottom: theme('spacing.24'),
          },
        },
        '.container-fluid': {
          maxWidth: '100vw',
          width: '100%',
          paddingLeft: '0',
          paddingRight: '0',
        },
      });
    }),
  ],
  safelist: [
    {
      pattern: /bg-.*/,
    },
    'heading-1',
    'heading-2',
    'heading-3',
    'heading-4',
    'heading-5',
    'heading-6',
    'paragraph',
    'paragraph-small',
    'paragraph-large',
    'text-hero',
    'list-disc',
    'list-decimal',
    'list-inside',
    'list-outside',
  ],
} as const;
